import * as React from "react";
import { useState } from "react";
import { Button, Checkbox, Input } from "../..";
import { PlusCircle, X } from "lucide-react";
import { Z_INDEX_CLASSES } from "../../lib/z-index";

export interface TableFilterItem {
  label: string;
  count?: number;
}

export interface TableFilterProps {
  items: TableFilterItem[];
  selectedItems?: string[];
  onSelectionChange?: (selectedItems: string[]) => void;
  placeholder?: string;
  className?: string;
  numberOfFilters?: 1 | 2 | 3;
  filterLabels?: string[];
  filterItems?: TableFilterItem[][]; // Different items for each filter
  selectedItemsByFilter?: string[][]; // Separate selections for each filter
  onFilterSelectionChange?: (filterIndex: number, selectedItems: string[]) => void;
  hideButtons?: boolean; // Hide the filter trigger buttons (for embedded use)
}

export function TableFilter({
  items,
  selectedItems = [],
  onSelectionChange,
  placeholder = "Search statuses...",
  className = "",
  numberOfFilters = 1,
  filterLabels = ["Status", "Tags", "Journeys"],
  filterItems,
  selectedItemsByFilter,
  onFilterSelectionChange,
  hideButtons = false,
}: TableFilterProps) {
  const [openFilterIndex, setOpenFilterIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [buttonRefs, setButtonRefs] = React.useState<(HTMLButtonElement | null)[]>([]);
  const [hoveredIndex, setHoveredIndex] = useState(0);

  const getFilterItems = (filterIndex: number) => {
    if (filterItems && filterItems[filterIndex]) {
      return filterItems[filterIndex];
    }
    return items;
  };

  const filteredItems = getFilterItems(openFilterIndex || 0).filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getFilterSelectedItems = (filterIndex: number) => {
    if (selectedItemsByFilter && selectedItemsByFilter[filterIndex]) {
      return selectedItemsByFilter[filterIndex];
    }
    return selectedItems;
  };

  const toggleItem = (value: string) => {
    if (openFilterIndex !== null && selectedItemsByFilter && onFilterSelectionChange) {
      // Use per-filter selection
      const currentFilterItems = selectedItemsByFilter[openFilterIndex] || [];
      const newSelection = currentFilterItems.includes(value)
        ? currentFilterItems.filter((item) => item !== value)
        : [...currentFilterItems, value];

      onFilterSelectionChange(openFilterIndex, newSelection);
    } else {
      // Use global selection (backward compatibility)
      const newSelection = selectedItems.includes(value)
        ? selectedItems.filter((item) => item !== value)
        : [...selectedItems, value];

      onSelectionChange?.(newSelection);
    }
  };

  const handleClickOutside = React.useCallback(
    (event: MouseEvent) => {
      const clickedOutside = buttonRefs.every((ref) => !ref?.contains(event.target as Node));
      if (clickedOutside) {
        const dropdown = document.getElementById(`table-filter-dropdown-${openFilterIndex}`);
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setOpenFilterIndex(null);
        }
      }
    },
    [buttonRefs, openFilterIndex]
  );

  React.useEffect(() => {
    if (openFilterIndex !== null) {
      setHoveredIndex(0); // Reset to first item when opening
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openFilterIndex, handleClickOutside]);

  React.useEffect(() => {
    // Reset hovered index when search changes
    setHoveredIndex(0);
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchQuery("");
  };

  const resetFilters = () => {
    if (selectedItemsByFilter && onFilterSelectionChange) {
      // Reset all filters
      for (let i = 0; i < numberOfFilters; i++) {
        onFilterSelectionChange(i, []);
      }
    } else {
      onSelectionChange?.([]);
    }
  };

  const getAllSelectedItems = () => {
    if (selectedItemsByFilter) {
      return selectedItemsByFilter.flat();
    }
    return selectedItems;
  };

  const setButtonRef = (index: number) => (ref: HTMLButtonElement | null) => {
    setButtonRefs((prev) => {
      const newRefs = [...prev];
      newRefs[index] = ref;
      return newRefs;
    });
  };

  const renderFilterButton = (filterIndex: number) => {
    const isOpen = openFilterIndex === filterIndex;
    const label = filterLabels[filterIndex] || `Filter ${filterIndex + 1}`;
    const filterSelectedItems = getFilterSelectedItems(filterIndex);

    return (
      <div key={filterIndex} className="relative">
        <Button
          ref={setButtonRef(filterIndex)}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 border-dashed"
          style={{ borderStyle: "dashed" }}
          onClick={() => setOpenFilterIndex(isOpen ? null : filterIndex)}
        >
          <PlusCircle className="h-4 w-4" />
          {label}
          {filterSelectedItems.length === 1 && (
            <span className="ml-1 bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-md">
              {filterSelectedItems[0]}
            </span>
          )}
          {filterSelectedItems.length === 2 && (
            <div className="ml-1 flex items-center gap-1">
              <span className="bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-md">
                {filterSelectedItems[0]}
              </span>
              <span className="bg-accent text-accent-foreground text-xs font-medium px-2 py-1 rounded-md">
                {filterSelectedItems[1]}
              </span>
            </div>
          )}
          {filterSelectedItems.length >= 3 && (
            <span className="ml-1 bg-accent text-accent-foreground text-xs px-2 py-1 rounded-md">
              {filterSelectedItems.length} selected
            </span>
          )}
        </Button>

        {isOpen && (
          <div
            id={`table-filter-dropdown-${filterIndex}`}
            className={`absolute left-0 top-full mt-1 rounded-md border bg-popover shadow-md ${Z_INDEX_CLASSES.dropdown} min-w-[200px]`}
          >
            <div className="p-2 border-b">
              <div className="relative">
                <Input
                  placeholder={placeholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-8 text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-[200px] overflow-y-auto p-1">
              {filteredItems.map((item, index) => (
                <button
                  key={item.label}
                  onClick={() => toggleItem(item.label)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                    index === hoveredIndex ? "bg-accent text-accent-foreground" : ""
                  }`}
                >
                  <Checkbox
                    checked={filterSelectedItems.includes(item.label)}
                    onCheckedChange={() => toggleItem(item.label)}
                  />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.count !== undefined && <span className="text-sm text-muted-foreground">{item.count}</span>}
                </button>
              ))}
              {filteredItems.length === 0 && (
                <div className="p-2 text-sm text-muted-foreground text-center">No items found</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // If hideButtons is true, just show the filter menu content
  if (hideButtons && items.length > 0) {
    return (
      <div className={className}>
        <div className="p-2 border-b">
          <div className="relative">
            <Input
              placeholder={placeholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 text-sm"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[200px] overflow-y-auto p-1">
          {filteredItems.map((item, index) => (
            <button
              key={item.label}
              onClick={() => toggleItem(item.label)}
              onMouseEnter={() => setHoveredIndex(index)}
              className={`flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                index === hoveredIndex ? "bg-accent text-accent-foreground" : ""
              }`}
            >
              <Checkbox checked={selectedItems.includes(item.label)} onCheckedChange={() => toggleItem(item.label)} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.count !== undefined && <span className="text-sm text-muted-foreground">{item.count}</span>}
            </button>
          ))}
          {filteredItems.length === 0 && (
            <div className="p-2 text-sm text-muted-foreground text-center">No items found</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {Array.from({ length: numberOfFilters }, (_, index) => renderFilterButton(index))}

      {getAllSelectedItems().length > 0 && (
        <Button variant="ghost" size="sm" className="flex items-center gap-2" onClick={resetFilters}>
          Reset
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
